import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { WorkoutService } from '@app-training/data-access/infra/workout.service';
import { catchError, concatMap, groupBy, iif, map, mergeMap, of, pipe, retry, switchMap, tap, throwError, timeout, timer } from 'rxjs';
import { LocalStorageService } from '@app-services/localStorage.service';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/angular';
import { IExercise, ISeriesLoadProgression, ISeriesSaveFailure, ITimeValues, IUpdateCurrentLoad, IUpdateLoad, IWorkout, IWorkoutItem, IWorkoutPlan, IWorkoutWeekLog } from '@app-training/data-access/entities/workouts.interface';
import { isUnansweredSeriesSave, SERIES_SAVE_TIMEOUT_MS } from '@app-training/data-access/series-save.util';
import { PendingSeriesSave, SeriesSaveOutbox } from '@app-training/data-access/series-save-outbox';
import { AlertStore } from '@shared/components/alert/data-access/alert.store';
import { ErrorHandlerService } from '@app-services/error-handler.service';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@shared/stores/prop-state.store';
import Timer, { TimeCounter } from 'easytimer.js';
import { Router } from '@angular/router';
import { EAlertModes, EAlertTypes } from '@shared/components/alert/data-access/entities/alert.enum';
import { HapticsStore } from '@shared/stores/haptics.store';
import { ImpactStyle } from '@capacitor/haptics';
import { tapResponse } from '@ngrx/operators';

interface IWorkoutState {
    ActiveWorkoutPlan: IWorkoutPlan | null;
    SelectedWorkout: IWorkout | null;
    WorkoutOfTheDay: IWorkout | null;
    Exercises: Record<string, IExercise>;
    WorkoutTimer: Record<string, Timer>;
    WorkoutWeekLog: IWorkoutWeekLog[] | null;
    RequestFeedback: boolean;
    SeriesSaveFailure: ISeriesSaveFailure | null;
};

const initialState: IWorkoutState = {
    ActiveWorkoutPlan: null,
    SelectedWorkout: null,
    WorkoutOfTheDay: null,
    Exercises: {},
    WorkoutTimer: {},
    WorkoutWeekLog: null,
    RequestFeedback: false,
    SeriesSaveFailure: null
};

/**
 * O SentryErrorInterceptor só reporta 5xx e descarta `status: 0` de propósito,
 * por ser ruído genérico (ver sentry-error.interceptor.ts). Numa gravação de
 * série não é ruído: é treino do aluno que se perdeu sem deixar rastro em lugar
 * nenhum. Reporta-se aqui justamente o que o interceptor deixa passar — status 0
 * e 4xx — como `warning` e com fingerprint fixo, para tudo cair numa issue só em
 * vez de uma por aparelho. 5xx fica de fora para não duplicar o evento que o
 * interceptor já envia.
 */
function reportSeriesSaveFailureToSentry(
    workoutItem: IWorkoutItem,
    attemptedSeriesPerformeds: number,
    revertedToSeriesPerformeds: number,
    error: unknown,
    attempts: number
): void {
    const status = error instanceof HttpErrorResponse ? error.status : null;
    if (status !== null && status >= 500) return;

    const statusLabel = status === null ? 'unknown' : String(status);

    Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTag('feature', 'workout-series');
        scope.setTag('status_code', statusLabel);

        // A stack aqui é sintética e idêntica em todos os eventos; sem fingerprint
        // o Sentry ainda assim agruparia tudo junto, mas o fixo garante que a issue
        // não se parta quando a mensagem mudar.
        scope.setFingerprint(['workout-series-save-failed', statusLabel]);

        scope.setContext('series', {
            trainingItemId: workoutItem.TrainingItemId,
            exerciseId: workoutItem.ExerciseId,
            attemptedSeriesPerformeds,
            revertedToSeriesPerformeds,
            totalSeries: workoutItem.SeriesLoadsProgressions?.length ?? null,
            // 1 = falhou à primeira e não era repetível (4xx/5xx). Acima disso, as
            // repetições correram e a série perdeu-se à mesma.
            attempts
        });

        Sentry.captureMessage(`[workout] series not saved — POST /Workout/UpdateSeries failed (HTTP ${statusLabel})`);
    });
}

/**
 * Esperas entre as repetições inline. O número de tentativas sai do tamanho da
 * lista, para as duas não poderem divergir.
 */
const SERIES_SAVE_RETRY_DELAYS_MS = [1_000, 3_000];

/**
 * A 500 on /UpdateCurrentLoad is a transient server fault, and the payload is a
 * plain set of absolute loads, so replaying it cannot double-apply anything.
 * Every other outcome (4xx, status 0) falls straight through to the catchError.
 */
const CURRENT_LOAD_RETRY_DELAYS_MS = [1_000, 3_000];

function isRetriableCurrentLoadSave(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 500;
}

/**
 * PersonId da sessão guardada, ou `null` quando não há sessão utilizável.
 *
 * A chave 'authorization' é escrita por auth.store.ts com JSON.stringify, mas o
 * que se lê de volta não é confiável: o storage sobrevive a atualizações do app
 * (pode ter sido gravado por uma versão com outro formato), é editável pelo
 * usuário, e o LocalStorageService ainda cai num espelho em memória quando o
 * WebView bloqueia site data. Um JSON.parse cru nisso lança SyntaxError DENTRO
 * do switchMap — e como o único catchError do fluxo está no pipe interno, o erro
 * sobe até o rxMethod e mata a subscrição de vez: a tela de treino fica sem
 * plano, sem erro visível e sem se recuperar até o app reiniciar.
 *
 * Todo caminho de falha devolve `null`, que o chamador já trata como "sem
 * sessão" — o mesmo comportamento de quando a chave não existe.
 */
function readSessionPersonId(localStorageService: LocalStorageService): string | null {
    const rawAuthorization = localStorageService.getItem('authorization');
    if (!rawAuthorization) return null;

    let authorization: unknown;
    try {
        authorization = JSON.parse(rawAuthorization);
    } catch {
        // Não é JSON: valor corrompido, truncado, ou a string 'undefined' que uma
        // gravação anterior possa ter deixado (JSON.parse('undefined') lança).
        return null;
    }

    // JSON.parse aceita escalares: 'null', '42' e '"abc"' parseiam sem lançar e
    // nenhum deles tem PersonId. Só um objeto interessa daqui para frente.
    if (typeof authorization !== 'object' || authorization === null) return null;

    // PersonId alimenta getWorkoutPlanActiveByPersonId(id: string); string vazia
    // ou de outro tipo viraria uma URL inválida, então vale como "sem sessão".
    const personId = (authorization as { PersonId?: unknown }).PersonId;
    return typeof personId === 'string' && personId ? personId : null;
}

/**
 * Ordinal comparison by Flag — the only order the domain defines for trainings.
 * The backend sorts by Flag to pick the workout of the day (`OrderBy(t => t.Flag)`
 * in WorkoutAppService.GetWorkoutOfTheDayId) but never sorts the collection it
 * returns, so `Trainings` arrives in storage order.
 */
function byFlag(a: IWorkout, b: IWorkout): number {
    const flagA = a.Flag ?? '';
    const flagB = b.Flag ?? '';
    return flagA < flagB ? -1 : flagA > flagB ? 1 : 0;
}

/** DayOfWeekTraining, the backend enum carried by `Training.DayOfWeek`: 1 = Sunday … 7 = Saturday. */
function currentDayOfWeekTraining(): number {
    return (DateTime.now().weekday % 7) + 1;
}

/**
 * The plan's trainings as a list that is safe to walk. A payload with a missing
 * or holed `Trainings` would otherwise throw while ordering, and an exception
 * here takes the whole training screen down.
 */
function planTrainings(plan: IWorkoutPlan | null): IWorkout[] {
    return Array.isArray(plan?.Trainings) ? plan.Trainings.filter(Boolean) : [];
}

/**
 * Port of WorkoutAppService.GetWorkoutOfTheDayId — the rotation rule of the domain.
 *
 * Used only when the store has no workout of the day to point at, which is the
 * window this port exists for: finishing a training clears the card and updates
 * `LastPerfomedOn` locally, but `shouldRefreshActiveWorkout` only refetches once
 * a day, so until tomorrow the payload's `WorkoutOfTheDayId` still names the
 * training just performed. Re-running the rule over the local state answers what
 * the next fetch will.
 */
function getWorkoutOfTheDayId(plan: IWorkoutPlan | null): string | null {
    const trainings = planTrainings(plan);
    if (!trainings.length) return null;
    if (trainings.length === 1) return trainings[0].TrainingId;
    /* `NextWorkoutId` is nullable and so is a training's id in a malformed
     * payload — without the guard `null === null` would answer the professor's
     * choice where there is none. */
    if (plan.NextWorkoutId && trainings.some(training => training.TrainingId === plan.NextWorkoutId)) {
        return plan.NextWorkoutId;
    }

    const byFlagOrder = [...trainings].sort(byFlag);
    const today = currentDayOfWeekTraining();
    const scheduledForToday = byFlagOrder.filter(training => training.DayOfWeek === today);
    const rotation = scheduledForToday.length ? scheduledForToday : byFlagOrder;

    if (rotation.every(training => !training.LastPerfomedOn)) return rotation[0].TrainingId;

    const lastPerformed = trainings
        .filter(training => !!training.LastPerfomedOn)
        .reduce((latest, training) =>
            DateTime.fromISO(training.LastPerfomedOn) > DateTime.fromISO(latest.LastPerfomedOn) ? training : latest);

    const next = rotation.find(training => byFlag(training, lastPerformed) > 0);

    return (next ?? rotation[0]).TrainingId;
}

/**
 * The trainings in the order the student will perform them: the Flag order
 * rotated to start at the workout of the day, which is how the backend advances
 * the rotation — the first Flag greater than the last performed one, wrapping
 * back to the first when there is none.
 *
 * The workout of the day anchors the rotation and is dropped from the list,
 * since it already has its own card above it. It is also the authority while it
 * exists: the payload computed it server-side, with the server's clock and
 * calendar, so the card and the list can never tell different stories. Only when
 * the store has no card — after finishing a training, or having trained today
 * before opening the app — does the local port of the rule answer, and then the
 * whole rotation is listed, starting at the one to be done next.
 */
function getUpcomingWorkouts(plan: IWorkoutPlan | null, workoutOfTheDay: IWorkout | null): IWorkout[] {
    const trainings = planTrainings(plan).sort(byFlag);
    if (!trainings.length) return [];

    const workoutOfTheDayId = workoutOfTheDay?.TrainingId ?? getWorkoutOfTheDayId(plan);
    const pivot = trainings.findIndex(training => training.TrainingId === workoutOfTheDayId);
    const start = pivot < 0 ? 0 : pivot;

    return [...trainings.slice(start), ...trainings.slice(0, start)]
        .filter(training => training.TrainingId !== workoutOfTheDay?.TrainingId);
}

export const WorkoutStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withComputed(({ ActiveWorkoutPlan, WorkoutOfTheDay }) => ({
        UpcomingWorkouts: computed(() => getUpcomingWorkouts(ActiveWorkoutPlan(), WorkoutOfTheDay())),
    })),
    withMethods((
        store,
        workoutService = inject(WorkoutService),
        localStorageService = inject(LocalStorageService),
        alertStore = inject(AlertStore),
        errorHandlerService = inject(ErrorHandlerService),
        hapticsStore = inject(HapticsStore),
        router = inject(Router),
        seriesSaveOutbox = inject(SeriesSaveOutbox),
    ) => {
        const updateSetItems = (workout: IWorkout) => {
            const setItemsBySet: Record<number, IWorkoutItem[]> = {};
            workout.TrainingItems = workout.TrainingItems.map(workoutItem => {
                workoutItem.SetOrder = workoutItem.SetOrder || 1;
                workoutItem.SeriesPerformeds = workoutItem.SeriesPerformeds || 0;
                if (!workoutItem.SeriesLoadsProgressions?.length) {
                    workoutItem.SeriesLoadsProgressions = [{
                        Repetition: '',
                        Load: '',
                        SeriesLoadProgressionId: '',
                        Time: '',
                        Velocity: '',
                        CurrentSerie: 1,
                        TrainingItemId: workoutItem.TrainingItemId
                    }]
                }
                workoutItem.SeriesLoadsProgressions.forEach((el, index) => {
                    el.CurrentSerie = index + 1;
                });
                if (workoutItem.Set) {
                    if (setItemsBySet[workoutItem.Set]) {
                        const firstItem = setItemsBySet[workoutItem.Set][0];
                        if (workoutItem.SeriesLoadsProgressions?.length !== firstItem.SeriesLoadsProgressions?.length) {
                            workoutItem.SeriesLoadsProgressions = firstItem.SeriesLoadsProgressions?.map((serieFirstItem, index) => {
                                const serie = workoutItem.SeriesLoadsProgressions?.[index];
                                if (serie) return serie;
                                const newSerie: ISeriesLoadProgression = {
                                    Repetition: '',
                                    Load: '',
                                    SeriesLoadProgressionId: '',
                                    Time: '',
                                    Velocity: '',
                                    CurrentSerie: serieFirstItem.CurrentSerie,
                                    TrainingItemId: serieFirstItem.TrainingItemId
                                }
                                return newSerie;
                            });
                        }
                        setItemsBySet[workoutItem.Set].push(workoutItem)
                         return null;
                    } else {
                        setItemsBySet[workoutItem.Set] = [workoutItem];
                    }
                }
                return workoutItem;
            }).filter(Boolean);
            workout.SetItems = setItemsBySet;
            return workout;
        };

        const alreadyTrainedToday = (ActiveWorkoutPlan: IWorkoutPlan): boolean => {
            return !!ActiveWorkoutPlan.Trainings.find(workout => {
                const today = DateTime.now().endOf('day');

                const lastPerfomedOn = DateTime.fromISO(workout.LastPerfomedOn).endOf('day');
                const diffLastPerfomedOn = today.diff(lastPerfomedOn, 'days').days;

                const LastPrintedOn = DateTime.fromISO(workout.LastPrintedOn).endOf('day');
                const diffLastPrintedOn = today.diff(LastPrintedOn, 'days').days;

                return diffLastPrintedOn === 0 || diffLastPerfomedOn === 0;
            });
        }

        const startWorkout = rxMethod<string>(pipe(
            tap(() => patchState(store, { ...setPropLoading('startWorkout') })),
            switchMap((trainingId) => {
                return workoutService.startWorkout(trainingId).pipe(
                    tap(() => {
                        patchState(store, { ...setPropLoaded('startWorkout') });
                    }),
                    catchError((error) => {
                        patchState(store, { ...setPropError('startWorkout') });
                        errorHandlerService.handleError(error);
                        return of(error);
                    })
                )
            })
        ))

        /**
         * O POST /Workout/UpdateSeries é o único caminho pelo qual a contagem de
         * séries chega ao servidor — nem o FinishTraining nem o UpdateLoad reenviam
         * esse número. Quando ele falha, o valor otimista já gravado em
         * `workoutItem.SeriesPerformeds` vira mentira: o card marca 3/3 e o servidor
         * ficou em 2/3. Aqui a escrita local é desfeita, o aluno é avisado e a perda
         * passa a ser rastreável.
         */
        const handleSeriesSaveFailure = (
            workoutItem: IWorkoutItem,
            attemptedSeriesPerformeds: number,
            previousSeriesPerformeds: number,
            error: unknown,
            attempts: number
        ): void => {
            workoutItem.SeriesPerformeds = previousSeriesPerformeds;

            // O item é o mesmo objeto que está em SelectedWorkout, então o template
            // do card já relê o valor reposto. O carrossel, que é imperativo, não —
            // é para ele que este sinal existe (ver card-exercise.component.ts).
            patchState(store, {
                SeriesSaveFailure: {
                    TrainingItemId: workoutItem.TrainingItemId,
                    AttemptedSeriesPerformeds: attemptedSeriesPerformeds,
                    RevertedToSeriesPerformeds: previousSeriesPerformeds
                }
            });

            // id fixo: com a rede do ginásio fora, cada série marcada abriria um
            // alerta novo. O AlertStore indexa por id, então as repetições colapsam.
            alertStore.openAlert({
                id: 'workout-series-save-failed',
                title: 'Série não salva',
                mode: EAlertModes.ALERT,
                type: EAlertTypes.WARNING,
                message: 'Não conseguimos registrar a série. A contagem voltou ao valor anterior — confira sua conexão e marque de novo.'
            });

            reportSeriesSaveFailureToSentry(workoutItem, attemptedSeriesPerformeds, previousSeriesPerformeds, error, attempts);
        };

        const stopWorkoutTimer = (id: string) => {
            store.WorkoutTimer()[id]?.stop();
            const localTimeValuesStr = localStorageService.getItem('workout-time-values');
            const localTimeValuesObj = localTimeValuesStr ? JSON.parse(localTimeValuesStr) : {};
            delete localTimeValuesObj[id];
            localStorageService.setItem('workout-time-values', JSON.stringify(localTimeValuesObj));
        }

        return {
            getWorkoutPlan: rxMethod<void>(pipe(
                switchMap(() => {
                    const personId = readSessionPersonId(localStorageService);
                    if (!personId) return of(null);
                    return workoutService.getWorkoutPlanActiveByPersonId(personId).pipe(
                        tap((ActiveWorkoutPlan) => {
                            if (ActiveWorkoutPlan) {
                                const localTimeValuesStr = localStorageService.getItem('workout-time-values');
                                const localTimeValuesObj = localTimeValuesStr ? JSON.parse(localTimeValuesStr) : {};
                                ActiveWorkoutPlan.Trainings = ActiveWorkoutPlan.Trainings.map(training => {
                                    training = updateSetItems(training);

                                    if (!training.isWorkoutStarted) {
                                        const workoutTime = localTimeValuesObj[training.TrainingId] as ITimeValues;

                                        const exerciseHasMinDuration = workoutTime?.timeValues?.minutes >= 2;
                                        const completedExercises = training.TrainingItems.filter(item => item.SeriesPerformeds >= item.SeriesLoadsProgressions.length);

                                        training.isWorkoutStarted = (exerciseHasMinDuration && completedExercises.length >= 1) || completedExercises.length >= 2;

                                        if (!training.isWorkoutStarted) {
                                            delete localTimeValuesObj[training.TrainingId];
                                        }
                                    }
                                    return training;
                                });
                                localStorageService.setItem('workout-time-values', JSON.stringify(localTimeValuesObj));
                                const WorkoutOfTheDay = alreadyTrainedToday(ActiveWorkoutPlan) ? null : ActiveWorkoutPlan.Trainings.find(training => training.TrainingId === ActiveWorkoutPlan.WorkoutOfTheDayId);
                                patchState(store, { WorkoutOfTheDay });
                            }
                            patchState(store, { ActiveWorkoutPlan, ...setPropLoaded('ActiveWorkoutPlan') });
                        }),
                        catchError((error) => {
                            patchState(store, { ActiveWorkoutPlan: null, ...setPropError('ActiveWorkoutPlan') });
                            errorHandlerService.handleError(error);
                            return of(error);
                        })
                    )
                }
                )
            )),

            selectWorkout: (id: string) => {
                const workout = store.ActiveWorkoutPlan()?.Trainings.find(training => training.TrainingId === id);
                patchState(store, { SelectedWorkout: workout });
                return workout;
            },

            updateSeriesPerformed: rxMethod<{ workoutItem: IWorkoutItem, seriesPerformed: number }>(pipe(
                // A escrita local logo abaixo é otimista: acontece antes da requisição
                // e sobrescreve SeriesPerformeds. Guardar o valor anterior aqui é o que
                // torna possível desfazê-la quando o POST falha — a partir do tap ele
                // já não existe mais em lugar nenhum.
                map((payload) => {
                    // Se já há uma gravação em fila para este exercício, a nova herda o
                    // previousSeriesPerformed dela — o último valor REALMENTE salvo — e não
                    // a contagem otimista atual, que ainda não chegou ao servidor. Sem isto,
                    // desistir de uma segunda marcação reverteria para um valor que o
                    // servidor nunca teve.
                    const queued = seriesSaveOutbox.peek(payload.workoutItem.TrainingItemId);
                    return {
                        ...payload,
                        previousSeriesPerformed: queued?.previousSeriesPerformed ?? payload.workoutItem.SeriesPerformeds,
                    };
                }),
                tap(({ workoutItem, seriesPerformed }) => {
                    // Uma marcação nova supersede o que estiver na fila para este
                    // exercício, para a antiga não chegar fora de ordem ao servidor.
                    seriesSaveOutbox.discard(workoutItem.TrainingItemId);
                    workoutItem.SeriesPerformeds = seriesPerformed
                    const { Series, SeriesPerformeds } = workoutItem;

                    if (SeriesPerformeds === Series) {
                        hapticsStore.triggerImpact(ImpactStyle.Light);
                    }

                    const workout = store.SelectedWorkout();

                    if (workout && !workout.isWorkoutStarted) {
                        const timer = store.WorkoutTimer()[workout.TrainingId];
                        const exerciseHasMinDuration = (timer?.getTimeValues().minutes ?? 0) >= 2;
                        const completedExercises = workout.TrainingItems.filter(item => item.SeriesPerformeds >= item.SeriesLoadsProgressions.length);

                        if (completedExercises && ((exerciseHasMinDuration && completedExercises.length >= 1) || completedExercises.length >= 2)) {
                            startWorkout(workout.TrainingId);
                            workout.isWorkoutStarted = true;
                        }

                        patchState(store, { SelectedWorkout: workout });
                    }
                }),
                // Uma marcação por exercício, em vez de uma para o ecrã inteiro. Antes
                // isto era um `switchMap` sobre a stream toda: como o store é de raiz e
                // cada card partilha este mesmo rxMethod, marcar a série do exercício B
                // abortava o POST do exercício A. E abortar não emite erro — o
                // `tapResponse` abaixo não corria, logo não havia reverter, nem alerta,
                // nem evento. A escrita otimista de A ficava e o servidor nunca soubera.
                // Não era preciso o aluno ser rápido: o `finishRest()` do card dispara
                // sozinho quando o cronómetro de descanso chega a zero, e vários cards
                // contam ao mesmo tempo.
                //
                // `groupBy` + `mergeMap` isolam exercícios distintos; o `concatMap` de
                // dentro serializa o mesmo exercício, para que duas marcações seguidas
                // cheguem na ordem em que foram feitas. Os grupos vivem enquanto o
                // rxMethod viver — são um por exercício tocado na sessão, dezenas.
                groupBy(({ workoutItem }) => workoutItem.TrainingItemId),
                mergeMap((seriesOfOneItem) => seriesOfOneItem.pipe(
                    concatMap(({ workoutItem, seriesPerformed, previousSeriesPerformed }) => {
                        // Conta as tentativas para o evento do Sentry poder dizer que as
                        // repetições correram e ainda assim não passaram. Fica no contexto
                        // e não na mensagem, senão o fingerprint parte e perde-se a série
                        // histórica da issue.
                        let attempts = 1;

                        return workoutService.updateSeriesPerformed(workoutItem.TrainingItemId, seriesPerformed).pipe(
                            timeout({ first: SERIES_SAVE_TIMEOUT_MS }),
                            retry({
                                count: SERIES_SAVE_RETRY_DELAYS_MS.length,
                                delay: (error, retryCount) => {
                                    if (!isUnansweredSeriesSave(error)) return throwError(() => error);
                                    attempts = retryCount + 1;
                                    return timer(SERIES_SAVE_RETRY_DELAYS_MS[retryCount - 1]);
                                }
                            }),
                            tapResponse({
                                // A success proves the network works now, so drain any
                                // series that got queued during a prior outage.
                                next: () => seriesSaveOutbox.flush(),
                                error: (error) => {
                                    console.error(error);
                                    if (isUnansweredSeriesSave(error)) {
                                        // Outage sustentado: mantém a contagem otimista e
                                        // enfileira. O flush reenvia na reconexão. Sem reverter
                                        // nem alertar aqui.
                                        Sentry.addBreadcrumb({
                                            category: 'workout-series',
                                            level: 'info',
                                            message: 'série enfileirada para reenvio',
                                            data: { trainingItemId: workoutItem.TrainingItemId, seriesPerformed, attempts },
                                        });
                                        seriesSaveOutbox.enqueue({
                                            workoutItem,
                                            seriesPerformed,
                                            previousSeriesPerformed,
                                            attempts,
                                            enqueuedAt: Date.now(),
                                        });
                                    } else {
                                        // Resposta de facto (4xx/5xx) ou erro não-rede: perda real.
                                        handleSeriesSaveFailure(workoutItem, seriesPerformed, previousSeriesPerformed, error, attempts);
                                    }
                                }
                            })
                        );
                    })
                )),
            )),

            getExerciseById: rxMethod<string>(pipe(
                switchMap((exerciseId) => iif(
                    () => !store.Exercises()[exerciseId],
                    workoutService.getExerciseById(exerciseId).pipe(
                        tap((exercise) => {
                            patchState(store, { Exercises: { ...store.Exercises(), [exerciseId]: exercise } });
                        }),
                        catchError((error) => {
                            patchState(store, { ActiveWorkoutPlan: null, ...setPropError('ActiveWorkoutPlan') });
                            errorHandlerService.handleError(error);
                            return of(error);
                        })
                    ),
                    of(null)
                ))
            )),

            updateLoad: rxMethod<IUpdateLoad[]>(pipe(
                tap(() => patchState(store, { ...setPropLoading('UpdateCharge') })),
                switchMap((payload) => {
                    return workoutService.updateLoad(payload).pipe(
                        tap(() => {
                            const workout = store.SelectedWorkout();

                            if (workout) {
                                const workoutItem = workout.TrainingItems.find(item => item.TrainingItemId === payload[0].TrainingItemId);
                                if (workoutItem) {
                                    workoutItem.SeriesLoadsProgressions = workoutItem.SeriesLoadsProgressions?.map(serie => {
                                        const correspondingPayloadItem = payload.find(item => item.SeriesLoadProgressionId === serie.SeriesLoadProgressionId);
                                        if (correspondingPayloadItem) {
                                            serie.Load = correspondingPayloadItem.Load;
                                        }
                                        return serie;
                                    });

                                    patchState(store, { SelectedWorkout: workout, ...setPropLoaded('UpdateCharge') });
                                }
                            }
                        }),
                        catchError((error) => {
                            patchState(store, { ...setPropError('finishWorkout') });
                            errorHandlerService.handleError(error);
                            return of(error);
                        })
                    )
                })
            )),

            updateCurrentLoad: rxMethod<IUpdateCurrentLoad[]>(pipe(
                tap(() => patchState(store, { ...setPropLoading('UpdateCharge') })),
                switchMap((payload) => {
                    return workoutService.updateCurrentLoad(payload).pipe(
                        retry({
                            count: CURRENT_LOAD_RETRY_DELAYS_MS.length,
                            delay: (error, retryCount) => {
                                if (!isRetriableCurrentLoadSave(error)) return throwError(() => error);
                                return timer(CURRENT_LOAD_RETRY_DELAYS_MS[retryCount - 1]);
                            }
                        }),
                        tap(() => {
                            const workout = store.SelectedWorkout();

                            if (workout) {
                                const workoutItem = workout.TrainingItems.find(item => item.TrainingItemId === payload[0].TrainingItemId);
                                if (workoutItem) {
                                    workoutItem.SeriesLoadsProgressions = workoutItem.SeriesLoadsProgressions?.map(serie => {
                                        const correspondingPayloadItem = payload.find(item => item.SeriesLoadProgressionId === serie.SeriesLoadProgressionId);
                                        if (correspondingPayloadItem) {
                                            serie.CurrentLoad = correspondingPayloadItem.CurrentLoad;
                                        }
                                        return serie;
                                    });

                                    patchState(store, { SelectedWorkout: workout, ...setPropLoaded('UpdateCharge') });
                                }
                            }
                        }),
                        catchError((error) => {
                            patchState(store, { ...setPropError('UpdateCharge') });
                            errorHandlerService.handleError(error);
                            return of(error);
                        })
                    )
                })
            )),

            startWorkoutTimer: (key: string) => {
                const timer = store.WorkoutTimer()[key] ?? new Timer();
                const localTimeValuesStr = localStorageService.getItem('workout-time-values');
                const localTimeValuesObj = localTimeValuesStr ? JSON.parse(localTimeValuesStr) : {};
                timer.removeAllEventListeners();

                timer.on('minutesUpdated', () => {
                    const workout = store.SelectedWorkout();
                    if (workout && !workout.isWorkoutStarted) {
                        const exerciseHasMinDuration = timer.getTimeValues().minutes >= 2;
                        const completedExercises = workout.TrainingItems.filter(item => item.SeriesPerformeds >= item.SeriesLoadsProgressions.length);

                        if (completedExercises && ((exerciseHasMinDuration && completedExercises.length >= 1) || completedExercises.length >= 2)) {
                            startWorkout(workout.TrainingId);
                            workout.isWorkoutStarted = true;
                        }
                    }
                });

                timer.on('secondsUpdated', () => {
                    localTimeValuesObj[key] = { timeValues: timer.getTimeValues(), updateOn: DateTime.now().toISO() };
                    localStorageService.setItem('workout-time-values', JSON.stringify(localTimeValuesObj));
                });

                timer.start({ startValues: localTimeValuesObj[key]?.timeValues || {} });

                patchState(store, { WorkoutTimer: { ...store.WorkoutTimer(), [key]: timer } });
            },

            pauseWorkoutTimer: (id: string) => {
                store.WorkoutTimer()[id]?.pause();
            },

            stopWorkoutTimer,

            removeOldTimeValues: () => {
                const localTimeValuesStr = localStorageService.getItem('workout-time-values');
                const localTimeValuesObj = localTimeValuesStr ? JSON.parse(localTimeValuesStr) : {};
                const newLocalTimeValuesObj = Object.entries<{ timeValues: TimeCounter, updateOn: string }>(localTimeValuesObj).reduce((acc, [key, value]) => {
                    if (DateTime.fromISO(value.updateOn).plus({ hours: 24 }) < DateTime.now()) {
                        return acc;
                    }
                    return { ...acc, [key]: value };
                }, {});
                localStorageService.setItem('workout-time-values', JSON.stringify(newLocalTimeValuesObj));
            },

            loadingActiveWorkoutPlan: () => {
                patchState(store, { ...setPropLoading('ActiveWorkoutPlan') })
            },

            finishWorkout: rxMethod<string>(pipe(
                tap(() => patchState(store, { ...setPropLoading('finishWorkout') })),
                switchMap((trainingId) => {
                    return workoutService.finishWorkout(trainingId).pipe(
                        tap((response) => {
                            hapticsStore.triggerImpact(ImpactStyle.Heavy);
                            alertStore.openAlert({
                                title: 'Treino concluído!',
                                mode: EAlertModes.ALERT,
                                message: `O treino de ${store.SelectedWorkout()?.Name || `Treino ${store.SelectedWorkout()?.Flag}`} acaba de ser concluído. Continue para o próximo treino.`,
                                type: EAlertTypes.SUCCESS
                            });
                            const ActiveWorkoutPlan = store.ActiveWorkoutPlan();
                            ActiveWorkoutPlan?.Trainings.map(training => {
                                if (training.TrainingId === trainingId) {
                                    training.LastPerfomedOn = DateTime.now().toISO();
                                    training.TrainingItems = training.TrainingItems.map(item => ({ ...item, SeriesPerformeds: 0 }));
                                    training.isWorkoutStarted = false;
                                }
                                return training;
                            })

                            if (ActiveWorkoutPlan && response?.SessionsWorkoutPlanPerformed) {
                                ActiveWorkoutPlan!.SessionsWorkoutPlanPerformed = response.SessionsWorkoutPlanPerformed;
                            }

                            const WorkoutWeekLog = store.WorkoutWeekLog();
                            WorkoutWeekLog?.forEach(log => {
                                if (log.Day === DateTime.now().toISODate()) log.Trained = true;
                            });

                            patchState(store, { ActiveWorkoutPlan: ActiveWorkoutPlan ? { ...ActiveWorkoutPlan } : null, WorkoutWeekLog });
                            patchState(store, {
                                WorkoutOfTheDay: null,
                                RequestFeedback: !localStorageService.getItem('feedbackGiven'),
                                ...setPropLoaded('finishWorkout')
                            });
                            router.navigate(['/training']);
                            stopWorkoutTimer(trainingId);
                        }),
                        catchError((error) => {
                            patchState(store, { ...setPropError('finishWorkout') });
                            errorHandlerService.handleError(error);
                            return of(error);
                        })
                    )
                })
            )),

            clearActiveWorkoutPlan: () => {
                patchState(store, { ActiveWorkoutPlan: null }, setPropInit('ActiveWorkoutPlan'));
            },

            getWeekLogByPersonId: rxMethod<string>(pipe(
                tap(() => patchState(store, { ...setPropLoading('WorkoutWeekLog') })),
                switchMap((personId) => {
                    return workoutService.getWeekLogByPersonId(personId).pipe(
                        tap((response) => {
                            patchState(store, { WorkoutWeekLog: response, ...setPropLoaded('WorkoutWeekLog') });
                        }),
                        catchError((error) => {
                            patchState(store, { ...setPropError('WorkoutWeekLog') });
                            errorHandlerService.handleError(error);
                            return of(error);
                        })
                    )
                })
            )),

            shouldRefreshActiveWorkout(): boolean {
                const lastRefresh = localStorageService.getItem('LastRefresh');
                if (!lastRefresh) return true;

                const lastRefreshDate = new Date(lastRefresh);
                const currentDate = new Date();


                return (
                    lastRefreshDate.getFullYear() !== currentDate.getFullYear() ||
                    lastRefreshDate.getMonth() !== currentDate.getMonth() ||
                    lastRefreshDate.getDate() !== currentDate.getDate()
                );
            },

            activeWorkoutRefreshed(): void {
                const currentDate = new Date();
                localStorageService.setItem('LastRefresh', `${currentDate}`);
            },

            clearRequestFeedback(): void {
                patchState(store, { RequestFeedback: false });
            },

            abandonQueuedSeriesSave(entry: PendingSeriesSave, error: unknown): void {
                handleSeriesSaveFailure(entry.workoutItem, entry.seriesPerformed, entry.previousSeriesPerformed, error, entry.attempts);
            },

            resetStore(): void {
                patchState(store, initialState, setPropInit('ActiveWorkoutPlan', 'UpdateCharge', 'finishWorkout', 'startWorkout', 'WorkoutWeekLog'));
            }
        };
    }),
    withHooks({
        onInit(store) {
            const outbox = inject(SeriesSaveOutbox);
            outbox.start({
                onGaveUp: (entry, error) => store.abandonQueuedSeriesSave(entry, error),
            });
        },
    }),
    withReqState('ActiveWorkoutPlan', 'UpdateCharge', 'finishWorkout', 'startWorkout', 'WorkoutWeekLog'),
);
